import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import useTranslation from '../../customHooks/translations';
import ListErrors from '../ListErrors';
import IconButton from '@mui/material/IconButton';
import { DEFAULT_THEME_COLOR } from '../../constants/theme';
import Typography from '@mui/material/Typography';
import FreakLocalization from './FreakLocalization';
import UploadFiles from '../UploadFiles';
import agent from '../../agent';
import { FREAK_PLACES_SUBMITTED } from '../../constants/actionTypes';
import FreakParticipantsList from './FreakParticipantsList';
import FreakSelect from './FreakSelect';
import TextField from '@mui/material/TextField';
import InputBase from '@mui/material/InputBase';
import {
  getMapObject,
  validateFacebookPlaceLink,
  validateInstagramLink,
  validatePlaceLink,
  validateYouTubeChannelLink,
} from '../../utils/freakUtils';
import DeleteIcon from '@mui/icons-material/Delete';
import YouTubeIcon from '@mui/icons-material/YouTube';
import FacebookIcon from '@mui/icons-material/Facebook';
import InstagramIcon from '@mui/icons-material/Instagram';
import LanguageIcon from '@mui/icons-material/Language';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import PlaceIcon from '@mui/icons-material/Place';
import LinkIcon from '@mui/icons-material/Link';
import LockIcon from '@mui/icons-material/Lock';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';
import {
  COMPOSER_COLORS,
  ComposerAddBar,
  ComposerInfoLine,
  ComposerMediaGrid,
  ComposerPanel,
  ComposerShell,
  ComposerTitleInput,
  composerText,
} from './FreakComposerParts';

// ─── Shared styles ────────────────────────────────────────────────────────────

const inputSx = {
  '& .MuiOutlinedInput-root': {
    color: 'white',
    '& fieldset': { borderColor: 'rgba(255,255,255,0.18)' },
    '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.35)' },
    '&.Mui-focused fieldset': { borderColor: DEFAULT_THEME_COLOR },
  },
  '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.45)' },
  '& .MuiInputLabel-root.Mui-focused': { color: DEFAULT_THEME_COLOR },
  '& .MuiFormHelperText-root': { color: '#f87171' },
};

const LinkInputRow = ({ icon, label, value, onChange, isValid, invalidMsg, onClear, testId }) => (
  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mt: 1.5 }}>
    <Box sx={{ pt: 0.5, flexShrink: 0 }}>{icon}</Box>
    <TextField
      size="small"
      label={label}
      variant="outlined"
      fullWidth
      value={value}
      onChange={onChange}
      error={!isValid}
      helperText={!isValid ? invalidMsg : undefined}
      inputProps={{ 'data-testid': testId }}
      sx={inputSx}
    />
    <IconButton
      size="small"
      onClick={onClear}
      sx={{ mt: 0.5, color: 'rgba(255,255,255,0.3)', '&:hover': { color: '#f87171' } }}
    >
      <DeleteIcon fontSize="small" />
    </IconButton>
  </Box>
);

// ─── Component ────────────────────────────────────────────────────────────────

const FreakPlacesEditor = (props) => {
  const translation = useTranslation();
  const t = translation.freakPlacesEditor;
  const tf = translation.freakEditor;
  const context = props.context;
  const navigate = useNavigate();
  const { slug } = useParams();

  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [participants, setParticipants] = useState([]);
  const [localization, setLocalization] = useState(null);
  const [images, setImages] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [openPanels, setOpenPanels] = useState({});
  const [imageToRemove, setImageToRemove] = useState(null);

  const [isValidPlaceLink, setIsValidPlaceLink] = useState(true);
  const [placeLink, setPlaceLink] = useState('');

  const [isValidYouTubeLink, setIsValidYouTubeLink] = useState(true);
  const [youTubeLink, setYouTubeLink] = useState('');

  const [isValidFacebookLink, setIsValidFacebookLink] = useState(true);
  const [facebookLink, setFacebookLink] = useState('');

  const [isValidInstagramLink, setIsValidInstagramLink] = useState(true);
  const [instagramLink, setInstagramLink] = useState('');

  useEffect(() => {
    const fetchCurrentMapObject = async () => {
      const mapObject = await getMapObject();
      setLocalization(mapObject);
    };
    if (!slug && !localization) fetchCurrentMapObject();
  }, []);

  const handleImageUpload = (file) => {
    setIsDirty(true);
    setImages((prev) => [...prev, {
      id: file?.data?.id,
      imageUrl: file?.data?.fileUrl,
      name: file?.data?.filename,
    }]);
  };

  useEffect(() => {
    const fetchData = async () => {
      if (slug) {
        const formData = await agent.FreaksPlaces.get(slug, context);
        const place = formData?.freakPlace;
        if (place) {
          setTitle(place.title || '');
          setBody(place.body || '');
          setImages(place.images || []);
          setParticipants(place.participants || []);
          setLocalization(place.map || null);
          setPlaceLink(place.placeSiteUrl || '');
          setYouTubeLink(place.placeYoutubeUrl || '');
          setFacebookLink(place.placeFacebookUrl || '');
          setInstagramLink(place.placeInstagramUrl || '');
        }
      }
    };
    fetchData();
  }, []);

  const onLocationSelect = (localizationMeta) => {
    setIsDirty(true);
    setLocalization(localizationMeta);
  };

  const handleParticipantSelect = (incoming) => {
    const updated = [...participants];
    for (const p of incoming) {
      if (!updated.find(u => u.username === p.username)) updated.push(p);
    }
    setIsDirty(true);
    setParticipants(updated);
  };

  const hasLocation = !!(localization?.coordinates || localization?.description);
  const canSave = !!title.trim() && !!body.trim() && hasLocation && !isUploading;
  const linksCount = [placeLink, youTubeLink, facebookLink, instagramLink].filter(Boolean).length;

  // a tool is shown when opened from "Add to your place" or when it already holds something;
  // location is required, so it stays open until there is one
  const isShown = {
    photos: !!openPanels.photos || images.length === 0,
    people: !!openPanels.people || participants.length > 0,
    location: !!openPanels.location || !hasLocation,
    links: !!openPanels.links || linksCount > 0,
  };

  const openPanel = (key) => {
    setOpenPanels(current => ({ ...current, [key]: true }));
    setTimeout(() => document.getElementById(`composer-panel-${key}`)?.scrollIntoView?.({ behavior: 'smooth', block: 'center' }), 50);
  };
  const closePanel = (key) => setOpenPanels(current => ({ ...current, [key]: false }));

  const options = [
    { key: 'photos', icon: <PhotoLibraryIcon />, color: COMPOSER_COLORS.photo, label: tf.photo },
    { key: 'people', icon: <GroupAddIcon />, color: COMPOSER_COLORS.people, label: t.addPeople },
    { key: 'location', icon: <PlaceIcon />, color: COMPOSER_COLORS.location, label: t.location },
    { key: 'links', icon: <LinkIcon />, color: COMPOSER_COLORS.links, label: t.links },
  ].map(option => ({ ...option, active: isShown[option.key], onClick: () => openPanel(option.key) }));

  const removeImage = (image) => {
    // a saved image lives on the server, one uploaded in this composer only needs to leave the list
    if (image?.id && slug) {
      setImageToRemove(image);
      return;
    }
    setImages(current => current.filter(item => item !== image));
  };

  const confirmRemoveImage = async () => {
    const image = imageToRemove;
    setImageToRemove(null);
    setImages(current => current.filter(item => item !== image));
    await agent.Images.del(image.id);
  };

  const submitForm = (event) => {
    event.preventDefault();
    if (!canSave) return;
    const freakPlace = {
      title,
      body,
      // the API looks participants up by username
      participants: participants.map(participant => ({ username: participant.username })),
      context,
      map: localization || {},
      images: images.map(img => img.name),
      public: false,
      placeLink,
      youTubeLink,
      facebookLink,
      instagramLink,
    };
    const slugObj = { slug };
    const promise = slug
      ? agent.FreaksPlaces.update(Object.assign(freakPlace, slugObj))
      : agent.FreaksPlaces.create(freakPlace);
    setIsDirty(false);
    props.onSubmit(promise);
  };

  const withDirty = (fn) => (e) => {
    setIsDirty(true);
    fn(e);
  };

  return (
    <ComposerShell
      title={slug ? t.editTitle : t.createTitle}
      actionLabel={slug ? t.update : t.save}
      canSubmit={canSave}
      onSubmit={submitForm}
      isDirty={isDirty}
      onLeave={() => navigate(-1)}
      leftNavigationOpen={props.leftNavigationOpen}
      errors={<ListErrors errors={props.errors} />}
      hint={!canSave && !isUploading ? t.required : null}
      texts={{ discardTitle: t.discardTitle, discardMessage: tf.discardMessage, keepEditing: tf.keepEditing, discard: tf.discard }}
    >
      {/* ── Photos first: a place is mostly what it looks like ── */}
      {images.length > 0 && <ComposerMediaGrid images={images} onRemove={removeImage} />}
      {isShown.photos && (
        <ComposerPanel id="composer-panel-photos" icon={<PhotoLibraryIcon />} color={COMPOSER_COLORS.photo}
                       title={t.addPhotos} onClose={images.length ? () => closePanel('photos') : undefined}>
          <UploadFiles onFileUpload={handleImageUpload} setSaveDisabled={setIsUploading} dest="freakPlaces" />
        </ComposerPanel>
      )}

      {/* ── Name · visibility · description ── */}
      <ComposerTitleInput
        testId="place-title"
        placeholder={t.namePlaceholder}
        value={title}
        onChange={(value) => {
          setIsDirty(true);
          setTitle(value);
        }}
        sx={{ mt: 2.5 }}
      />
      <ComposerInfoLine icon={<LockIcon />}>{t.privateInfo}</ComposerInfoLine>
      <InputBase
        fullWidth
        multiline
        minRows={3}
        inputProps={{ 'data-testid': 'place-description' }}
        placeholder={t.descriptionPlaceholder}
        value={body}
        onChange={(e) => {
          setIsDirty(true);
          setBody(e.target.value);
        }}
        sx={{ mt: 2, color: composerText.primary, fontSize: '1.05rem' }}
      />

      {/* ── Location (required) ── */}
      {isShown.location ? (
        <ComposerPanel id="composer-panel-location" icon={<PlaceIcon />} color={COMPOSER_COLORS.location}
                       title={hasLocation ? t.location : t.pickLocation}
                       onClose={hasLocation ? () => closePanel('location') : undefined}>
          <FreakLocalization localization={localization} onSelect={onLocationSelect} />
        </ComposerPanel>
      ) : (
        <Box
          role="button"
          tabIndex={0}
          data-testid="place-location-summary"
          onClick={() => openPanel('location')}
          onKeyDown={(e) => { if (e.key === 'Enter') openPanel('location'); }}
          sx={{
            display: 'flex', alignItems: 'center', gap: 1, mt: 2, px: 1.5, py: 1, borderRadius: 2, cursor: 'pointer',
            background: composerText.surface, '&:hover': { background: 'rgba(255,255,255,0.1)' },
          }}
        >
          <PlaceIcon sx={{ color: COMPOSER_COLORS.location }} />
          <Typography variant="body2" noWrap sx={{ color: composerText.primary }}>{localization.description}</Typography>
        </Box>
      )}

      {/* ── People ── */}
      {isShown.people && (
        <ComposerPanel id="composer-panel-people" icon={<GroupAddIcon />} color={COMPOSER_COLORS.people}
                       title={t.addPeople} onClose={participants.length ? undefined : () => closePanel('people')}>
          {participants.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <FreakParticipantsList participants={participants} />
            </Box>
          )}
          <FreakSelect onSelect={handleParticipantSelect} />
        </ComposerPanel>
      )}

      {/* ── Links: one of each kind ── */}
      {isShown.links && (
        <ComposerPanel id="composer-panel-links" icon={<LinkIcon />} color={COMPOSER_COLORS.links}
                       title={t.linksTitle} onClose={linksCount ? undefined : () => closePanel('links')}>
          <LinkInputRow
            testId="place-link-website"
            icon={<LanguageIcon sx={{ color: DEFAULT_THEME_COLOR, fontSize: 28 }} />}
            label={t.website}
            value={placeLink}
            onChange={withDirty((e) => validatePlaceLink(e.target.value, setIsValidPlaceLink, setPlaceLink))}
            isValid={isValidPlaceLink}
            invalidMsg={translation.linksPlace.placeholders.invalidLink}
            onClear={() => { setPlaceLink(''); setIsValidPlaceLink(true); }}
          />
          <LinkInputRow
            icon={<YouTubeIcon sx={{ color: '#FF0000', fontSize: 28 }} />}
            label={t.youtube}
            value={youTubeLink}
            onChange={withDirty((e) => validateYouTubeChannelLink(e.target.value, setIsValidYouTubeLink, setYouTubeLink))}
            isValid={isValidYouTubeLink}
            invalidMsg={translation.linksYoutube.placeholders.invalidLink}
            onClear={() => { setYouTubeLink(''); setIsValidYouTubeLink(true); }}
          />
          <LinkInputRow
            icon={<FacebookIcon sx={{ color: '#1877F2', fontSize: 28 }} />}
            label={t.facebook}
            value={facebookLink}
            onChange={withDirty((e) => validateFacebookPlaceLink(e.target.value, setIsValidFacebookLink, setFacebookLink))}
            isValid={isValidFacebookLink}
            invalidMsg={translation.linksFacebook.placeholders.invalidLink}
            onClear={() => { setFacebookLink(''); setIsValidFacebookLink(true); }}
          />
          <LinkInputRow
            icon={<InstagramIcon sx={{ color: '#E1306C', fontSize: 28 }} />}
            label={t.instagram}
            value={instagramLink}
            onChange={withDirty((e) => validateInstagramLink(e.target.value, setIsValidInstagramLink, setInstagramLink))}
            isValid={isValidInstagramLink}
            invalidMsg={translation.linksInstagram.placeholders.invalidLink}
            onClear={() => { setInstagramLink(''); setIsValidInstagramLink(true); }}
          />
        </ComposerPanel>
      )}

      {/* ── Add to your place ── */}
      <ComposerAddBar label={t.addToPlace} options={options} />

      <Dialog open={!!imageToRemove} onClose={() => setImageToRemove(null)}>
        <DialogTitle>{tf.removePhotoConfirm}</DialogTitle>
        <DialogActions>
          <Button onClick={() => setImageToRemove(null)}>{tf.keepEditing}</Button>
          <Button color="error" onClick={confirmRemoveImage}>{tf.remove}</Button>
        </DialogActions>
      </Dialog>
    </ComposerShell>
  );
};

const mapStateToProps = state => ({
  ...state.freakPlacesEditor,
  context: state.common.context,
  device: state.common.device,
  leftNavigationOpen: state.common.leftNavigationOpen,
});

const mapDispatchToProps = dispatch => ({
  onSubmit: payload => dispatch({ type: FREAK_PLACES_SUBMITTED, payload }),
});

export default connect(mapStateToProps, mapDispatchToProps)(FreakPlacesEditor);
